import React from 'react';
import Typography from '~/shared/components/Typography/Typography';
import { useTranslation } from '~/shared/utils/translation';
import styles from './PointsMeterBar.module.scss';
import { Anchor } from '~/shared/components/Anchor';
import BonusPointsCard from '~/shared/components/BonusPointsCard/BonusPointsCard';
import Watermark from '~/shared/components/Watermarks/Watermark';
import { TranslationKey } from '~/lib/data-contract';

interface PointsMeterBarProps {
    currentPoints: number;
    targetPoints: number;
    remainingPoints: number;
    rewardAmount: number;
    rewardCurrency?: string;
}

const PointsMeterBar: React.FC<PointsMeterBarProps> = ({
    currentPoints,
    targetPoints,
    //remainingPoints,
    rewardAmount,
    rewardCurrency = 'kr',
}) => {
    const { translate } = useTranslation();

    // Determine title key based on current points intervals
    let titleKey: TranslationKey;
    if (currentPoints >= 1 && currentPoints < 500) {
        titleKey = 'loyalty.meterBar.title.0to499';
    } else if (currentPoints >= 500 && currentPoints < 1000) {
        titleKey = 'loyalty.meterBar.title.500to999';
    } else if (currentPoints >= 1000 && currentPoints < 1500) {
        titleKey = 'loyalty.meterBar.title.1000to1449';
    } else if (currentPoints > 1500) {
        titleKey = 'loyalty.meterBar.title.goalReached';
    } else {
        // Fallback for 1500+ points
        titleKey = 'loyalty.meterBar.subtitle';
    }

    // Calculate percentage for progress bar
    const progressPercentage = Math.min((currentPoints / targetPoints) * 100, 100);

    // Determine arrow position based on percentage
    // Left-aligned at 0-15%, Centered at 16-84%, Right-aligned at 85-100%
    let arrowPosition: 'left' | 'center' | 'right';
    if (progressPercentage <= 15) {
        arrowPosition = 'left';
    } else if (progressPercentage >= 85) {
        arrowPosition = 'right';
    } else {
        arrowPosition = 'center';
    }

    return (
        <>
            <div className={styles.wrapper}>
                <Watermark type="Logo" className={styles.watermark} aria-hidden />
                <div className={styles.container}>
                    <div className={styles.innerContainer}>
                        <header className={styles.header}>
                            <Typography variant="title-S" as="h3" className={styles.headerText}>
                                {translate(titleKey)}
                            </Typography>
                            <Typography
                                variant="paragraph-S"
                                as="h4"
                                className={styles.rewardTipText}
                            >
                                {titleKey === 'loyalty.meterBar.subtitle'
                                    ? ''
                                    : translate('loyalty.meterBar.subtitle')}
                            </Typography>
                        </header>

                        <div className={styles.meterContainer}>
                            <div className={styles.meter}>
                                <div
                                    className={styles.progress}
                                    style={{ width: `${progressPercentage}%` }}
                                />
                                <div
                                    className={`${styles.indicatorTooltip} ${
                                        arrowPosition === 'left'
                                            ? styles.arrowLeft
                                            : arrowPosition === 'right'
                                              ? styles.arrowRight
                                              : styles.arrowCenter
                                    }`}
                                    style={{ left: `${progressPercentage}%` }}
                                >
                                    Du har {currentPoints} point
                                </div>
                            </div>
                        </div>

                        <div>
                            <div className={styles.targetPoints}>
                                <span className={styles.pointsHighlight}>{targetPoints} point</span>
                            </div>
                        </div>
                    </div>
                    <aside className={styles.rewardCardContainer}>
                        <BonusPointsCard
                            amount={rewardAmount}
                            currency={rewardCurrency}
                            alternateText="Bonusreward"
                            variant="small"
                            className={styles.rewardCard}
                        />
                    </aside>
                </div>
            </div>
            <footer className={styles.footer}>
                <Anchor href="/">{translate('loyalty.meterBar.link.advice')}</Anchor>
            </footer>
        </>
    );
};

export default PointsMeterBar;
